import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { canOpen } from '../lib/access'
import { isMobileDevice, isForceDesktop, setForceDesktop } from '../lib/device.js'
import { sfx } from '../composables/useSfx.js'
import { brandFeature } from '../brand.js'

const routes = [
  // The marketing page + self-signup — only for a brand that sells online
  // (brand.features.signup); otherwise its tenants are created from /admin.
  brandFeature('signup')
    ? { path: '/welcome', component: () => import('../views/LandingView.vue'), meta: { public: true } }
    : { path: '/welcome', redirect: '/login' },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('../layouts/AppLayout.vue'),
    children: [
      { path: '', redirect: '/dashboard' },
      { path: 'dashboard', name: 'dashboard', component: () => import('../views/DashboardView.vue') },
      { path: 'employees', name: 'employees', component: () => import('../views/EmployeesView.vue') },
      { path: 'employees/:id', name: 'employee-profile', component: () => import('../views/EmployeeProfileView.vue') },
      { path: 'departments', name: 'departments', component: () => import('../views/DepartmentsView.vue') },
      { path: 'positions', name: 'positions', component: () => import('../views/PositionsView.vue') },
      { path: 'leaves', name: 'leaves', component: () => import('../views/LeavesView.vue') },
      { path: 'leave-types', name: 'leave-types', component: () => import('../views/LeaveTypesView.vue') },
      { path: 'attendance', name: 'attendance', component: () => import('../views/AttendanceView.vue') },
      { path: 'timesheet', name: 'timesheet', component: () => import('../views/TimesheetView.vue') },
      { path: 'assistant', name: 'assistant', component: () => import('../views/TeamAssistantView.vue') },
      { path: 'ai-connectors', name: 'ai-connectors', component: () => import('../views/AiConnectorsView.vue') },
      { path: 'info-board', name: 'info-board', component: () => import('../views/CustomFieldsBoard.vue') },
      { path: 'documents', name: 'documents', component: () => import('../views/DocumentsView.vue') },
      { path: 'shifts', name: 'shifts', component: () => import('../views/ShiftPatternsView.vue') },
      // Lost from the router in July (3c1523a): the web could add a holiday from the
      // timesheet but never fix or remove one. TADQIQ-2026-11-04.
      { path: 'holidays', name: 'holidays', component: () => import('../views/HolidaysView.vue') },
      { path: 'payroll', name: 'payroll', component: () => import('../views/PayrollView.vue') },
      { path: 'advances', name: 'advances', component: () => import('../views/AdvancesView.vue') },
      { path: 'accounting', name: 'accounting', component: () => import('../views/AccountingView.vue') },
      { path: 'bonuses',  name: 'bonuses',  component: () => import('../views/BonusesView.vue') },
      { path: 'reports', name: 'reports', component: () => import('../views/ReportsView.vue') },
      { path: 'biometric', name: 'biometric', component: () => import('../views/BiometricView.vue') },
      { path: 'users', name: 'users', component: () => import('../views/UsersView.vue') },
      { path: 'admin', name: 'platform-admin', component: () => import('../views/PlatformAdminView.vue') },
      { path: 'roles', name: 'roles', component: () => import('../views/RolesView.vue') },
      { path: 'audit-log', name: 'audit-log', component: () => import('../views/AuditLogView.vue') },
      { path: 'disciplinary', name: 'disciplinary', component: () => import('../views/DisciplinaryView.vue') },
      { path: 'custody', name: 'custody', component: () => import('../views/CustodyView.vue') },
      { path: 'announcements', name: 'announcements', component: () => import('../views/AnnouncementsView.vue') },
      { path: 'location-requests', name: 'location-requests', component: () => import('../views/LocationRequestsView.vue') },
      { path: 'field-requests', name: 'field-requests', component: () => import('../views/FieldRequestsView.vue') },
      { path: 'notifications', name: 'notifications', component: () => import('../views/NotificationsView.vue') },
      { path: 'settings', name: 'settings', component: () => import('../views/SettingsView.vue') },
    ],
  },
  {
    path: '/m',
    component: () => import('../layouts/MobileLayout.vue'),
    children: [
      { path: '', name: 'm-dashboard', component: () => import('../views/mobile/MobileDashboard.vue') },
      { path: 'dashboard', component: () => import('../views/mobile/MobileDashboard.vue') },
      { path: 'attendance', name: 'm-attendance', component: () => import('../views/mobile/MyAttendanceView.vue') },
      { path: 'leaves', name: 'm-leaves', component: () => import('../views/mobile/MyLeavesView.vue') },
      { path: 'payroll', name: 'm-payroll', component: () => import('../views/mobile/MyPayrollView.vue') },
      { path: 'approvals', name: 'm-approvals', component: () => import('../views/mobile/ApprovalsView.vue') },
      { path: 'more', name: 'm-more', component: () => import('../views/mobile/MoreView.vue') },
      // ── Admin (staff) mobile dashboard ──
      { path: 'admin', name: 'm-admin', component: () => import('../views/mobile/AdminDashboard.vue') },
      { path: 'team', name: 'm-team', component: () => import('../views/mobile/TeamView.vue') },
      { path: 'timesheet', name: 'm-timesheet', component: () => import('../views/mobile/TimesheetView.vue') },
      { path: 'assistant', name: 'm-assistant', component: () => import('../views/mobile/TeamAssistantView.vue'), meta: { hideNav: true } },
      { path: 'info-board', name: 'm-info-board', component: () => import('../views/CustomFieldsBoard.vue') },
      { path: 'employees', name: 'm-employees', component: () => import('../views/mobile/EmployeesView.vue') },
      { path: 'employees/:id', name: 'm-employee-profile', component: () => import('../views/mobile/EmployeeProfileView.vue'), meta: { hideNav: true } },
      { path: 'employees/:id/attendance', name: 'm-employee-attendance', component: () => import('../views/mobile/EmployeeAttendanceView.vue'), meta: { hideNav: true } },
      { path: 'employees/:id/custody', name: 'm-employee-custody', component: () => import('../views/mobile/CustodyView.vue'), meta: { hideNav: true } },
      { path: 'notifications', name: 'm-notifications', component: () => import('../views/mobile/NotificationsView.vue') },
      { path: 'announcements', name: 'm-announcements', component: () => import('../views/mobile/AnnouncementsView.vue') },
      { path: 'roster', name: 'm-roster', component: () => import('../views/mobile/DailyRosterView.vue') },
      { path: 'field-requests', name: 'm-field-requests-admin', component: () => import('../views/mobile/FieldRequestsAdminView.vue') },
      { path: 'location-requests', name: 'm-location-requests', component: () => import('../views/LocationRequestsView.vue') },
      { path: 'field-request/:id', name: 'm-field-request', component: () => import('../views/mobile/FillFieldsView.vue') },
      { path: 'payroll-runs', name: 'm-payroll-runs', component: () => import('../views/mobile/PayrollRunsView.vue') },
      { path: 'my-ledger', name: 'm-my-ledger', component: () => import('../views/mobile/MyLedgerView.vue') },
      { path: 'ledger', name: 'm-ledger', component: () => import('../views/mobile/LedgerView.vue') },
      { path: 'custody', name: 'm-custody', component: () => import('../views/mobile/CustodyView.vue') },
      { path: 'export', name: 'm-export', component: () => import('../views/mobile/ExportView.vue') },
      { path: 'settings', name: 'm-settings', component: () => import('../views/mobile/SettingsView.vue') },
      { path: 'users', name: 'm-users', component: () => import('../views/mobile/UsersView.vue') },
      { path: 'departments', name: 'm-departments', component: () => import('../views/mobile/DepartmentsView.vue') },
      { path: 'positions', name: 'm-positions', component: () => import('../views/mobile/PositionsView.vue') },
    ],
  },
  { path: '/upload/:token', component: () => import('../views/MobileUploadView.vue'), meta: { public: true } },
  { path: '/terms', component: () => import('../views/legal/TermsView.vue'), meta: { public: true } },
  { path: '/privacy', component: () => import('../views/legal/PrivacyView.vue'), meta: { public: true } },
  { path: '/:catchAll(.*)', redirect: '/dashboard' },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore()
  if (to.meta.public) return next()
  // Recover a cookie-only session (localStorage evicted by iOS) before deciding.
  // No-ops after the first call and when a local session already exists.
  await auth.bootstrap()
  if (!auth.isAuthenticated) return next({ name: 'login', query: { redirect: to.fullPath } })
  // Employees only see the mobile shell. If they try to land on a desktop
  // route, redirect to /m. (They can still hit /login + /welcome.)
  if (auth.user?.role === 'employee' && !to.path.startsWith('/m')) {
    return next('/m')
  }
  // The platform owner (super_admin) belongs to no customer company: one place,
  // the platform console — no tenant pages, no mobile shell.
  if (auth.user?.role === 'super_admin') {
    return to.name === 'platform-admin' ? next() : next('/admin')
  }

  const isStaff = auth.user?.role && auth.user.role !== 'employee'

  // Entering the mobile shell re-arms auto-mobile (clears a prior "force
  // desktop" choice) so the toggle is intuitive: Desktop link → desktop sticks;
  // tapping into /m → mobile resumes.
  if (isStaff && to.path.startsWith('/m')) setForceDesktop(false)

  // Staff on a phone-sized screen get the mobile shell by default — UNLESS they
  // explicitly chose the full desktop site. Without this, staff always landed
  // on the desktop layout on their phones. The "الموقع الكامل" link sets the
  // override so this doesn't bounce them straight back.
  if (isStaff && isMobileDevice() && !isForceDesktop() && !to.path.startsWith('/m')) {
    return next('/m/admin')
  }

  // Staff (admin/hr/manager/...) landing on the bare mobile home get the
  // admin dashboard, not the employee self-service one.
  if (isStaff && (to.path === '/m' || to.path === '/m/dashboard')) {
    return next('/m/admin')
  }

  // A page the user's permissions don't open → their home (the menus hide it
  // already; this covers deep links and stale bookmarks).
  if (to.name && !canOpen(auth, to.name)) {
    return next(to.path.startsWith('/m') ? (isStaff ? '/m/admin' : '/m') : '/dashboard')
  }
  next()
})

// A subtle nav click on each successful route change — silent unless the user
// opted into tap sounds (sfx.tap defaults off). Skipped for the very first
// navigation (from === undefined) so a page load / deep-link isn't announced.
router.afterEach((to, from) => {
  if (from?.name != null) sfx('tap')
})

export default router
